import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Palette, Users, Calendar, Database, Plus, Edit2, Trash2, ChevronUp, ChevronDown, Shield, Key, UserX, UserCheck,
  Download, Upload, RefreshCw, AlertTriangle, Power, Clock, Save, X, HardHat,
} from 'lucide-react';
import { api, extractError } from '../lib/api';
import { fmtDate, fmtDateTime } from '../lib/date';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../lib/confirm';
import PersonDialog from '../components/PersonDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export default function SettingsPage() {
  const tabs = [
    { to: '/einstellungen/kostenstellen', label: 'Kostenstellen', icon: Palette },
    { to: '/einstellungen/benutzer', label: 'Benutzer & Zugänge', icon: Users },
    { to: '/einstellungen/berechtigungen', label: 'Berechtigungen', icon: Shield },
    { to: '/einstellungen/feiertage', label: 'Feiertage', icon: Calendar },
    { to: '/einstellungen/zeit', label: 'Zeit & Statistik', icon: Clock },
    { to: '/einstellungen/system', label: 'System & Datensicherung', icon: Database },
  ];
  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 border-b border-slate-200 overflow-x-auto">
        <div className="flex gap-1">
          {tabs.map(t => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-t-md border-b-2 ${isActive ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              data-testid={`settings-tab-${t.to}`}
            >
              <t.icon size={14} />
              {t.label}
            </NavLink>
          ))}
        </div>
      </div>
      <Routes>
        <Route index element={<Navigate to="kostenstellen" replace />} />
        <Route path="kostenstellen" element={<KostenstellenSettings />} />
        <Route path="benutzer" element={<BenutzerSettings />} />
        <Route path="berechtigungen" element={<BerechtigungenSettings />} />
        <Route path="feiertage" element={<FeiertageSettings />} />
        <Route path="zeit" element={<ZeitStatistikSettings />} />
        <Route path="system" element={<SystemSettings />} />
      </Routes>
    </div>
  );
}

// ================= Kostenstellen =================
function KostenstellenSettings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);

  const load = async () => {
    const r = await api.get('/cost-centers', { params: { include_inactive: true } });
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing({ name: '', short_code: '', color: '#2e7d32', type: 'Intern', responsible: '', active: true }); setOpen(true); };
  const openEdit = (k) => { setEditing({ ...k }); setOpen(true); };

  const save = async () => {
    try {
      if (editing.id) {
        const { id, created_at, deleted, sort_order, ...rest } = editing;
        await api.patch(`/cost-centers/${id}`, rest);
        toast.success('Kostenstelle aktualisiert.');
      } else {
        await api.post('/cost-centers', editing);
        toast.success('Kostenstelle angelegt.');
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setItems(reordered);
    try {
      await api.post('/cost-centers/sort', { ordered_ids: reordered.map(i => i.id) });
    } catch (err) { toast.error(extractError(err)); load(); }
  };

  const askDelete = async (k) => {
    try {
      const r = await api.get(`/cost-centers/${k.id}/dependencies`);
      setDeleteInfo({ kst: k, ...r.data });
    } catch (err) { toast.error(extractError(err)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/cost-centers/${deleteInfo.kst.id}`);
      toast.success('Kostenstelle gelöscht.');
      setDeleteInfo(null);
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const deactivate = async (k) => {
    try {
      await api.patch(`/cost-centers/${k.id}`, { active: !k.active });
      toast.success(k.active ? 'Kostenstelle deaktiviert.' : 'Kostenstelle aktiviert.');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="font-display text-lg">Kostenstellen verwalten</CardTitle>
        <Button onClick={openCreate} data-testid="create-kst-button"><Plus size={14} className="mr-1"/>Kostenstelle hinzufügen</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Reihenfolge</TableHead><TableHead>Farbe</TableHead><TableHead>Name</TableHead>
            <TableHead>Kürzel</TableHead><TableHead>Verantwortlich</TableHead><TableHead>Typ</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Aktionen</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.filter(i => !i.deleted).map((k, idx) => (
              <TableRow key={k.id} data-testid={`kst-row-${k.short_code}`}>
                <TableCell>
                  <div className="flex gap-0.5">
                    <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-6 w-6 p-0"><ChevronUp size={12}/></Button>
                    <Button size="sm" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="h-6 w-6 p-0"><ChevronDown size={12}/></Button>
                  </div>
                </TableCell>
                <TableCell><span className="inline-block w-5 h-5 rounded" style={{ background: k.color }}/></TableCell>
                <TableCell className="font-semibold">{k.name}</TableCell>
                <TableCell className="font-mono">{k.short_code}</TableCell>
                <TableCell>{k.responsible || '-'}</TableCell>
                <TableCell><Badge variant="outline">{k.type}</Badge></TableCell>
                <TableCell>{k.active ? <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(k)} data-testid={`edit-kst-${k.short_code}`}><Edit2 size={14}/></Button>
                  <Button size="sm" variant="ghost" onClick={() => deactivate(k)} title={k.active ? 'Deaktivieren' : 'Aktivieren'}><Power size={14}/></Button>
                  <Button size="sm" variant="ghost" onClick={() => askDelete(k)} className="text-rose-600" data-testid={`delete-kst-${k.short_code}`}><Trash2 size={14}/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {editing && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing.id ? 'Kostenstelle bearbeiten' : 'Kostenstelle hinzufügen'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="kst-name"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kürzel</Label><Input value={editing.short_code} onChange={e => setEditing({ ...editing, short_code: e.target.value })} data-testid="kst-shortcode"/></div>
                <div>
                  <Label>Typ</Label>
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="Intern">Intern</SelectItem><SelectItem value="Extern">Extern</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Verantwortlich</Label><Input value={editing.responsible || ''} onChange={e => setEditing({ ...editing, responsible: e.target.value })}/></div>
              <div>
                <Label>Farbe</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={editing.color} onChange={e => setEditing({ ...editing, color: e.target.value })} className="h-10 w-14 rounded border border-slate-200 cursor-pointer" data-testid="kst-color"/>
                  <Input value={editing.color} onChange={e => setEditing({ ...editing, color: e.target.value })} className="font-mono"/>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={save} data-testid="kst-save">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {deleteInfo && (
        <AlertDialog open={!!deleteInfo} onOpenChange={(v) => !v && setDeleteInfo(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kostenstelle "{deleteInfo.kst.name}" löschen?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  {(deleteInfo.employees?.length || deleteInfo.projects?.length || deleteInfo.future_assignments_count > 0) ? (
                    <>
                      <div className="text-rose-700 font-semibold flex items-center gap-1.5"><AlertTriangle size={14}/> Löschen nicht möglich – Abhängigkeiten vorhanden:</div>
                      {deleteInfo.employees.length > 0 && <div><strong>{deleteInfo.employees.length} Mitarbeiter:</strong> {deleteInfo.employees.map(e => `${e.first_name} ${e.last_name}`).join(', ')}</div>}
                      {deleteInfo.projects.length > 0 && <div><strong>{deleteInfo.projects.length} Projekte:</strong> {deleteInfo.projects.map(p => p.name).join(', ')}</div>}
                      {deleteInfo.future_assignments_count > 0 && <div><strong>Zukünftige Einsätze:</strong> {deleteInfo.future_assignments_count}</div>}
                      <div className="pt-2 text-slate-600">Bitte weisen Sie diese zuerst einer anderen Kostenstelle zu oder deaktivieren Sie die Kostenstelle.</div>
                    </>
                  ) : (
                    <div>Keine Abhängigkeiten. Diese Aktion kann nicht rückgängig gemacht werden.</div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              {!(deleteInfo.employees?.length || deleteInfo.projects?.length || deleteInfo.future_assignments_count > 0) && (
                <AlertDialogAction onClick={confirmDelete} className="bg-rose-700 hover:bg-rose-800">Endgültig löschen</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Card>
  );
}

// ================= Benutzer =================
function BenutzerSettings() {
  const { user: currentUser } = useAuth();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPass, setNewPass] = useState('');

  const load = async () => {
    const [u, c, e] = await Promise.all([
      api.get('/users'),
      api.get('/cost-centers'),
      api.get('/employees', { params: { include_inactive: true } }),
    ]);
    setUsers(u.data);
    setCostCenters(c.data);
    setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  const kstById = React.useMemo(() => Object.fromEntries(costCenters.map(c => [c.id, c])), [costCenters]);
  const empById = React.useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);

  // Anlegen läuft über denselben Dialog wie in der Mitarbeiterliste – damit
  // gibt es nur noch ein Formular für "Person anlegen".
  const [anlegenOpen, setAnlegenOpen] = useState(false);
  const openCreate = () => setAnlegenOpen(true);
  // Mitarbeiter-Datensatz zu einem bestehenden Zugang nachtragen. Name und
  // Kostenstelle kommen vom Server aus dem Zugang – hier werden nur die
  // Angaben erfragt, die es dort nicht gibt.
  const [maFuer, setMaFuer] = useState(null);
  const openEdit = (u) => {
    const emp = u.employee_id ? empById[u.employee_id] : null;
    setEditing({ ...u, password: '', cost_center_id: u.cost_center_id || null });
    setOpen(true);
  };

  const save = async () => {
    // Eine Anmeldekennung muss übrig bleiben. Der Server lehnt es ohnehin ab,
    // aber hier ist die Meldung verständlicher und steht direkt am Formular.
    const mail = (editing.email || '').trim();
    const name = (editing.username || '').trim();
    if (!mail && !name) {
      toast.error('Bitte eine E-Mail-Adresse oder einen Benutzernamen angeben.'); return;
    }
    if (mail && !mail.includes('@')) {
      toast.error('Die E-Mail-Adresse sieht nicht richtig aus.'); return;
    }
    try {
      // Normalise cost_center_id: '__all__' => null (Zugriff auf alle KSt)
      const payload = { ...editing, email: mail || null, username: name || null };
      if (payload.cost_center_id === '__all__' || payload.cost_center_id === '') payload.cost_center_id = null;
      if (editing.id) {
        const { id, created_at, force_password_change, last_login, password, ...rest } = payload;
        // rest enthält employee_id (Verknüpfung) und wird mitgespeichert
        await api.patch(`/users/${id}`, rest);
        toast.success('Benutzer aktualisiert.');
      } else {
        await api.post('/users', payload);
        toast.success('Benutzer angelegt. Temporäres Passwort gesetzt.');
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? 'Konto deaktiviert.' : 'Konto aktiviert.');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const resetPassword = async () => {
    if (!newPass) { toast.error('Neues Passwort angeben.'); return; }
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { new_password: newPass });
      toast.success('Passwort zurückgesetzt. Benutzer muss es beim nächsten Login ändern.');
      setResetUser(null); setNewPass('');
    } catch (err) { toast.error(extractError(err)); }
  };

  const deleteUser = async (u) => {
    // Hängt ein Mitarbeiter dran, muss entschieden werden, was mit dem
    // passiert. Nur den Zugang zu löschen hinterlässt sonst einen Mitarbeiter
    // ohne Anmeldung – einen Zustand, den die App eigentlich nicht vorsieht.
    const emp = u.employee_id ? empById[u.employee_id] : null;

    if (!emp) {
      if (!(await confirm({
        title: 'Benutzer löschen?',
        description: `${u.first_name} ${u.last_name} wird endgültig aus dem System entfernt.`,
        variant: 'destructive', confirmLabel: 'Löschen',
      }))) return;
      try {
        const r = await api.delete(`/users/${u.id}`);
        toast.success(r.data?.message || 'Benutzer gelöscht.');
        await load();
      } catch (err) { toast.error(extractError(err)); }
      return;
    }

    // Zwei Rückfragen hintereinander statt einer mit zwei Knöpfen: Der Dialog
    // kennt nur Bestätigen und Abbrechen, und Abbrechen landet auch bei Escape
    // oder Klick daneben. Eine Löschung ohne klares Ja darf dabei nicht
    // herauskommen – deshalb erst die Absicht klären, dann den Umfang.
    if (!(await confirm({
      title: `${u.first_name} ${u.last_name} löschen?`,
      description: `Der Zugang wird entfernt. Dazu gehört der Mitarbeiter-Datensatz `
        + `„${emp.first_name} ${emp.last_name}" – im nächsten Schritt wird gefragt, `
        + `ob der mitgehen soll.`,
      variant: 'destructive', confirmLabel: 'Weiter',
    }))) return;

    const mitMitarbeiter = await confirm({
      title: 'Den Mitarbeiter mit löschen?',
      description: 'Sind Einsätze, Abwesenheiten oder Zeiten erfasst, bleibt der '
        + 'Datensatz erhalten und wird nur deaktiviert – damit vergangene Monate '
        + 'nachvollziehbar bleiben. Ohne Historie wird er wirklich entfernt.',
      variant: 'destructive',
      confirmLabel: 'Ja, beides löschen',
      cancelLabel: 'Nein, nur den Zugang',
    });
    try {
      const r = await api.delete(`/users/${u.id}`, { params: { mitarbeiter_mit: !!mitMitarbeiter } });
      if (r.data?.geloescht === false) toast.warning(r.data.message);
      else toast.success(r.data?.message || 'Benutzer gelöscht.');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="font-display text-lg">Benutzer & Zugänge</CardTitle>
        <Button onClick={openCreate} data-testid="create-user-button"><Plus size={14} className="mr-1"/>Benutzer anlegen</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Benutzername</TableHead><TableHead>Rolle</TableHead>
            <TableHead>Zuordnung</TableHead>
            <TableHead>Status</TableHead><TableHead>Letzter Login</TableHead><TableHead className="text-right">Aktionen</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map(u => {
              const kst = u.cost_center_id ? kstById[u.cost_center_id] : null;
              return (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-semibold">{u.first_name} {u.last_name} {u.id === currentUser?.id && <Badge variant="outline" className="ml-1 text-xs">Sie</Badge>}</TableCell>
                <TableCell className="font-mono text-sm">{u.email || '–'}</TableCell>
                <TableCell className="font-mono text-sm">{u.username || '–'}</TableCell>
                <TableCell>{u.role === 'admin' ? <Badge className="bg-slate-900 text-white hover:bg-slate-900"><Shield size={10} className="mr-1"/>Administrator</Badge> : u.role === 'projektleiter' ? <Badge className="bg-sky-100 text-sky-900 border-sky-300 hover:bg-sky-100">Projektleiter</Badge> : <Badge variant="outline">Betrachter</Badge>}</TableCell>
                <TableCell>
                  {kst ? (
                    <Badge variant="outline" className="font-mono">
                      <span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: kst.color }}/>
                      {kst.short_code}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-100 text-slate-700">Alle Kostenstellen</Badge>
                  )}
                </TableCell>
                <TableCell>{u.active ? <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                <TableCell className="text-xs text-slate-600">{u.last_login ? fmtDateTime(u.last_login) : '-'}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Edit2 size={14}/></Button>
                  <Button size="sm" variant="ghost" onClick={() => setResetUser(u)} title="Passwort zurücksetzen"><Key size={14}/></Button>
                  {!u.employee_id && (
                    <Button size="sm" variant="ghost" onClick={() => setMaFuer(u)}
                            title="Mitarbeiter-Datensatz anlegen – für Planung und Abwesenheiten"
                            data-testid={`create-employee-${u.id}`}>
                      <HardHat size={14} className="text-sky-700"/>
                    </Button>
                  )}
                  {u.id !== currentUser?.id && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(u)}>{u.active ? <UserX size={14} className="text-rose-600"/> : <UserCheck size={14} className="text-emerald-600"/>}</Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} className="text-rose-600"><Trash2 size={14}/></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </CardContent>

      {/* Anlegen: gemeinsamer Dialog, identisch mit dem in der Mitarbeiterliste */}
      <PersonDialog open={anlegenOpen} onOpenChange={setAnlegenOpen} modus="benutzer" onSaved={load} />

      {maFuer && (
        <MitarbeiterNachtragenDialog
          benutzer={maFuer}
          costCenters={costCenters}
          onClose={() => setMaFuer(null)}
          onSaved={() => { setMaFuer(null); load(); }}
        />
      )}

      {editing && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Benutzer bearbeiten</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vorname</Label><Input value={editing.first_name} onChange={e => setEditing({ ...editing, first_name: e.target.value })}/></div>
                <div><Label>Nachname</Label><Input value={editing.last_name} onChange={e => setEditing({ ...editing, last_name: e.target.value })}/></div>
              </div>
              <div><Label>E-Mail</Label><Input type="email" value={editing.email || ''} onChange={e => setEditing({ ...editing, email: e.target.value })} data-testid="user-email"/></div>
              <div>
                <Label>Benutzername</Label>
                <Input value={editing.username || ''} onChange={e => setEditing({ ...editing, username: e.target.value })}
                       placeholder="z. B. b.kaufmann" data-testid="user-username"/>
                <p className="text-xs text-slate-500 mt-1">
                  Eines von beiden muss ausgefüllt bleiben – damit meldet sich der Benutzer an.
                </p>
              </div>
              {!editing.id && (
                <div><Label>Temporäres Passwort</Label><Input type="password" value={editing.password || ''} onChange={e => setEditing({ ...editing, password: e.target.value })} data-testid="user-password"/><p className="text-xs text-slate-500 mt-1">Mind. 10 Zeichen, 1 Zahl, 1 Buchstabe. Benutzer wird beim ersten Login zur Änderung aufgefordert.</p></div>
              )}
              <div>
                <Label>Rolle</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="projektleiter">Projektleiter</SelectItem>
                    <SelectItem value="viewer">Betrachter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zuordnung zu Kostenstelle</Label>
                <Select
                  value={editing.cost_center_id || '__all__'}
                  onValueChange={(v) => setEditing({ ...editing, cost_center_id: v === '__all__' ? null : v })}
                >
                  <SelectTrigger data-testid="user-costcenter"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Alle Kostenstellen (kein Filter)</SelectItem>
                    {costCenters.filter(c => !c.deleted && c.active).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.short_code} – {c.name} {c.type === 'Extern' ? '(Schabos/Extern)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Bestimmt den Standard-Filter auf der Plantafel. "Alle" = voller Überblick, einzelne KSt = auf diese KSt fokussiert, Schabos = Verleih-KSt.
                </p>
                {editing.employee_id && (
                  <p className="text-xs text-slate-500 mt-1">
                    Hängt ein Mitarbeiter am Zugang, wandert eine hier gewählte Kostenstelle
                    auch zu ihm – und umgekehrt. „Alle Kostenstellen" gilt nur für den Zugang:
                    Der Mitarbeiter behält seine, weil er sich sonst nicht einplanen ließe.
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })}/>
                Aktiv
              </label>
              {editing.id && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <Label className="text-sm font-medium">Verknüpfter Mitarbeiter</Label>
                  <Select
                    value={editing.employee_id || '__none__'}
                    onValueChange={(v) => setEditing({ ...editing, employee_id: v === '__none__' ? null : v })}
                  >
                    <SelectTrigger data-testid="user-linked-employee"><SelectValue placeholder="Kein Mitarbeiter"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Kein Mitarbeiter verknüpft</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} {e.active === false ? '(inaktiv)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Hier stand ein Häkchen „Aus Plantafel ausschließen", das in
                      Wahrheit den Mitarbeiter deaktiviert hat. Seit Deaktivieren
                      auch den Zugang sperrt, hätte dieses Häkchen den Benutzer
                      ausgesperrt, während man ihn gerade bearbeitet – ein
                      Nebeneffekt, den an dieser Stelle niemand erwartet.
                      Deaktiviert wird deshalb nur noch in der Mitarbeiterliste,
                      wo die Meldung sagt, was mit dem Zugang passiert. */}
                  <p className="text-xs text-slate-500">
                    Ob der Mitarbeiter in der Plantafel auftaucht, hängt daran, ob er aktiv ist.
                    Das wird in der Mitarbeiterliste umgestellt – dort sperrt das Deaktivieren
                    zugleich diesen Zugang. Vergangene Einsätze und Zeiten bleiben immer erhalten.
                  </p>
                </div>
              )}
              {!editing.id && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={editing.create_employee ?? false}
                      onChange={e => setEditing({ ...editing, create_employee: e.target.checked })}
                      data-testid="user-create-employee"
                    />
                    Zusätzlich einen Mitarbeiter für diese Person anlegen
                  </label>
                  <p className="text-xs text-slate-500">
                    Empfohlen für spätere App-Zugänge. So wird die Person unter „Mitarbeiter" geführt und kann verplant werden. Ohne Haken bleibt es ein reiner Login-Zugang.
                  </p>
                  {editing.create_employee && (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div>
                        <Label className="text-xs">Mitarbeiter-Typ</Label>
                        <Select value={editing.employee_type || 'Mitarbeiter'} onValueChange={(v) => setEditing({ ...editing, employee_type: v })}>
                          <SelectTrigger data-testid="user-employee-type"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            {['Mitarbeiter', 'Azubi', 'Leiharbeiter', 'Praktikant'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Zuordnung</Label>
                        <Select value={editing.employee_assignment_context || 'construction'} onValueChange={(v) => setEditing({ ...editing, employee_assignment_context: v })}>
                          <SelectTrigger data-testid="user-employee-context"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="construction">Baustellen</SelectItem>
                            <SelectItem value="device">Geräte</SelectItem>
                            <SelectItem value="office">Büro / Leitung</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={save} data-testid="user-save">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {resetUser && (
        <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Passwort zurücksetzen</DialogTitle><DialogDescription>Temporäres Passwort für {resetUser.first_name} {resetUser.last_name}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <Input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Neues Passwort" data-testid="reset-password-input"/>
              <p className="text-xs text-slate-500">Mind. 10 Zeichen, 1 Zahl, 1 Buchstabe. Benutzer muss es beim nächsten Login ändern.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResetUser(null); setNewPass(''); }}>Abbrechen</Button>
              <Button onClick={resetPassword} data-testid="reset-password-submit">Zurücksetzen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

// ================= Berechtigungen =================
const RESOURCE_LABELS = {
  dashboard: 'Dashboard', plantafel: 'Plantafel', device_plantafel: 'Geräte-Plantafel',
  projects: 'Projekte', employees: 'Mitarbeiter', devices: 'Geräte', absences: 'Abwesenheiten (Einträge)',
  time_import: 'Zeitimport', cost_centers: 'Kostenstellen', holidays: 'Feiertage',
  users: 'Benutzer', activity_log: 'Aktivität', settings: 'Einstellungen',
  absence_balances: 'Abwesenheiten: Richtlinien-Saldi',
  absence_policies: 'Abwesenheiten: Richtlinienzuordnungen', attendance_timesheets: 'Anwesenheit: Stundenzettel',
  day_view: 'Tagesansicht',
};
const ACTION_LABELS = { view: 'Ansehen', create: 'Anlegen', edit: 'Bearbeiten', delete: 'Löschen' };
// Erklärungen für Zeilen, in denen eine Spalte etwas anderes bedeutet als ihr
// Titel vermuten lässt. Eine eigene fünfte Spalte nur für die Tagesansicht
// wäre in allen anderen Zeilen leer – dann lieber ein Satz an der Zeile.
const RESOURCE_HINTS = {
  day_view: 'In dieser Zeile heißt "Bearbeiten": darf vor- und zurückblättern. '
    + 'Ohne dieses Recht ist nur der heutige Tag freigegeben.',
};
const EDITABLE_ROLES = [
  { key: 'projektleiter', label: 'Projektleiter' },
  { key: 'viewer', label: 'Betrachter' },
];

function BerechtigungenSettings() {
  const { loadPermissions } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [resources, setResources] = useState([]);
  const [adminOnly, setAdminOnly] = useState([]);
  const [actions, setActions] = useState([]);
  const [role, setRole] = useState('projektleiter');
  const [saving, setSaving] = useState(false);
  const [restrictions, setRestrictions] = useState({ projektleiter: false, viewer: false });

  const load = async () => {
    const [r, s] = await Promise.all([api.get('/permissions'), api.get('/settings')]);
    setMatrix(r.data.matrix || {});
    setResources(r.data.resources || []);
    setAdminOnly(r.data.admin_only_resources || []);
    setActions(r.data.actions || []);
    setRestrictions(s.data.role_cost_center_restrictions || { projektleiter: false, viewer: false });
  };
  useEffect(() => { load(); }, []);

  const toggle = (res, act) => {
    setMatrix(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[role]) next[role] = {};
      if (!next[role][res]) next[role][res] = {};
      next[role][res][act] = !next[role][res][act];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/settings', { permissions: matrix, role_cost_center_restrictions: restrictions });
      toast.success('Berechtigungen gespeichert.');
      await load();
      await loadPermissions();
    } catch (err) { toast.error(extractError(err)); } finally { setSaving(false); }
  };

  if (!matrix) return <div className="text-slate-500 text-sm">Lädt Berechtigungen…</div>;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2"><Shield size={18}/>Berechtigungen pro Rolle</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          Administratoren haben immer volle Rechte (nicht änderbar). Hier legen Sie fest, was Projektleiter und Betrachter dürfen. Monteure und Fahrer bekommen ebenfalls die Rolle „Betrachter“. Ausgegraute Zeilen sind fest Administratoren vorbehalten.
        </div>
        <div className="flex gap-1">
          {EDITABLE_ROLES.map(r => (
            <button
              key={r.key}
              onClick={() => setRole(r.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold border ${role === r.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid={`perm-role-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold rounded-md border border-slate-200 bg-white px-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 accent-slate-900"
            checked={!!restrictions[role]}
            onChange={() => setRestrictions(prev => ({ ...prev, [role]: !prev[role] }))}
            data-testid={`perm-restrict-kst-${role}`}
          />
          Nur eigene Kostenstelle anzeigen (HR-Übersichten auf die dem Benutzer zugeordnete Kostenstelle beschränken)
        </label>
        <div className="overflow-x-auto border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bereich</TableHead>
                {actions.map(a => <TableHead key={a} className="text-center">{ACTION_LABELS[a] || a}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {resources.map(res => {
                // Diese Bereiche sind fest Administratoren vorbehalten – das
                // Backend fragt die Matrix dort gar nicht. Schalter wären eine
                // Zusage, die die App nicht einhält, deshalb ausgegraut.
                const nurAdmin = adminOnly.includes(res);
                return (
                  <TableRow key={res} data-testid={`perm-row-${res}`} className={nurAdmin ? 'opacity-50' : undefined}>
                    <TableCell className="font-medium">
                      {RESOURCE_LABELS[res] || res}
                      {nurAdmin && <span className="ml-2 text-xs font-normal text-slate-500">nur Administratoren</span>}
                      {RESOURCE_HINTS[res] && (
                        <div className="text-xs font-normal text-slate-500 mt-0.5">{RESOURCE_HINTS[res]}</div>
                      )}
                    </TableCell>
                    {actions.map(act => (
                      <TableCell key={act} className="text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-slate-900 disabled:cursor-not-allowed"
                          disabled={nurAdmin}
                          checked={nurAdmin ? false : !!matrix[role]?.[res]?.[act]}
                          onChange={() => toggle(res, act)}
                          data-testid={`perm-${role}-${res}-${act}`}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div>
          <Button onClick={save} disabled={saving} data-testid="perm-save-button">
            <Save size={14} className="mr-1"/>{saving ? 'Speichere…' : 'Berechtigungen speichern'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Mitarbeiter-Datensatz zu einem bestehenden Zugang nachtragen.
 *
 * Die Gegenrichtung zu "Zugang nachreichen" in der Mitarbeiterliste. Gefragt
 * wird nur nach dem, was im Zugang nicht steht: Art, Einsatzbereich,
 * Eintrittsdatum. Name und Kostenstelle übernimmt der Server aus dem Zugang –
 * zweimal eingeben heißt zweimal Gelegenheit, es unterschiedlich zu tippen.
 */
function MitarbeiterNachtragenDialog({ benutzer, costCenters, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: 'Mitarbeiter',
    assignment_context: 'construction',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    cost_center_id: benutzer.cost_center_id || '',
  });
  const [saving, setSaving] = useState(false);
  const setzen = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const istBuero = form.assignment_context === 'office';
  const brauchtEnde = ['Leiharbeiter', 'Praktikant'].includes(form.type);

  const speichern = async () => {
    if (brauchtEnde && !form.end_date) {
      toast.error(`Für ${form.type} ist ein Enddatum Pflicht.`); return;
    }
    if (!istBuero && !form.cost_center_id) {
      toast.error('Für Baustellen- und Geräte-Mitarbeiter ist eine Kostenstelle Pflicht.'); return;
    }
    setSaving(true);
    try {
      await api.post(`/users/${benutzer.id}/employee`, {
        first_name: benutzer.first_name, last_name: benutzer.last_name,
        type: form.type,
        assignment_context: form.assignment_context,
        cost_center_id: form.cost_center_id || null,
        start_date: form.start_date,
        end_date: form.end_date || null,
      });
      toast.success(`Mitarbeiter-Datensatz für ${benutzer.first_name} ${benutzer.last_name} angelegt.`);
      onSaved();
    } catch (err) { toast.error(extractError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="mitarbeiter-nachtragen-dialog">
        <DialogHeader>
          <DialogTitle>Mitarbeiter-Datensatz für {benutzer.first_name} {benutzer.last_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Damit erscheint die Person in Abwesenheiten, Urlaubsanspruch und – je nach
            Einsatzbereich – in der Planung. Name und Kostenstelle werden aus dem Zugang
            übernommen.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Art</Label>
              <Select value={form.type} onValueChange={(v) => setzen('type', v)}>
                <SelectTrigger data-testid="nachtragen-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {['Mitarbeiter', 'Azubi', 'Leiharbeiter', 'Praktikant'].map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Einsatzbereich</Label>
              <Select value={form.assignment_context} onValueChange={(v) => setzen('assignment_context', v)}>
                <SelectTrigger data-testid="nachtragen-context"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="construction">Baustellen</SelectItem>
                  <SelectItem value="device">Geräte</SelectItem>
                  <SelectItem value="office">Büro / Leitung</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Kostenstelle {!istBuero && <span className="text-rose-600">*</span>}</Label>
            <Select value={form.cost_center_id || (istBuero ? '__none__' : '')}
                    onValueChange={(v) => setzen('cost_center_id', v === '__none__' ? '' : v)}>
              <SelectTrigger data-testid="nachtragen-kst"><SelectValue placeholder="Wählen…"/></SelectTrigger>
              <SelectContent>
                {istBuero && <SelectItem value="__none__">Keine – alle Kostenstellen</SelectItem>}
                {costCenters.filter(c => !c.deleted).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Eintrittsdatum</Label>
              <Input type="date" value={form.start_date}
                     onChange={e => setzen('start_date', e.target.value)} data-testid="nachtragen-start"/>
            </div>
            <div>
              <Label>Enddatum {brauchtEnde && <span className="text-rose-600">*</span>}</Label>
              <Input type="date" value={form.end_date}
                     onChange={e => setzen('end_date', e.target.value)} data-testid="nachtragen-ende"/>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={speichern} disabled={saving} data-testid="nachtragen-speichern">
            {saving ? 'Speichere…' : 'Anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================= Feiertage =================
function FeiertageSettings() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ date: '', name: '' });

  const load = async () => { const r = await api.get('/holidays', { params: { year } }); setItems(r.data); };
  useEffect(() => { load(); /* eslint-disable-line */ }, [year]);

  const toggle = async (h) => {
    try { await api.patch(`/holidays/${h.id}`, { active: !h.active }); await load(); }
    catch (err) { toast.error(extractError(err)); }
  };
  const del = async (h) => {
    try { await api.delete(`/holidays/${h.id}`); toast.success('Feiertag gelöscht.'); await load(); }
    catch (err) { toast.error(extractError(err)); }
  };
  const addManual = async () => {
    if (!addForm.date || !addForm.name) { toast.error('Datum und Name erforderlich.'); return; }
    try { await api.post('/holidays/manual', addForm); toast.success('Feiertag hinzugefügt.'); setAddOpen(false); setAddForm({ date: '', name: '' }); await load(); }
    catch (err) { toast.error(extractError(err)); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3 flex-wrap gap-2">
        <CardTitle className="font-display text-lg">Feiertage verwalten (NRW)</CardTitle>
        <div className="flex items-center gap-2">
          <Input type="number" className="w-28" value={year} onChange={e => setYear(Number(e.target.value))} data-testid="holiday-year"/>
          <Button onClick={() => setAddOpen(true)} data-testid="add-manual-holiday"><Plus size={14} className="mr-1"/>Feiertag hinzufügen</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Name</TableHead><TableHead>Quelle</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktion</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.sort((a,b) => a.date.localeCompare(b.date)).map(h => (
              <TableRow key={h.id}>
                <TableCell className="font-mono text-sm">{fmtDate(h.date)}</TableCell>
                <TableCell>{h.name}</TableCell>
                <TableCell>{h.source === 'system' ? <Badge variant="outline" className="bg-slate-100">System (automatisch)</Badge> : <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300">Manuell</Badge>}</TableCell>
                <TableCell>{h.active !== false ? <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => toggle(h)}>{h.active !== false ? 'Deaktivieren' : 'Aktivieren'}</Button>
                  {h.source !== 'system' && <Button size="sm" variant="ghost" onClick={() => del(h)} className="text-rose-600"><Trash2 size={14}/></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Feiertag hinzufügen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Datum</Label><Input type="date" value={addForm.date} onChange={e => setAddForm({ ...addForm, date: e.target.value })}/></div>
            <div><Label>Name</Label><Input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} placeholder="z.B. Betriebsurlaub"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Abbrechen</Button>
            <Button onClick={addManual}>Hinzufügen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ================= Zeit & Statistik =================
function ZeitStatistikSettings() {
  const [settings, setSettings] = useState(null);
  const [prefixes, setPrefixes] = useState([]);
  const [newPrefix, setNewPrefix] = useState('');
  const [dailyDefault, setDailyDefault] = useState(8);
  const [dailyLimit, setDailyLimit] = useState(8);
  const [lockDays, setLockDays] = useState(30);
  const [mehrfachPraefixe, setMehrfachPraefixe] = useState([]);
  const [neuesMehrfach, setNeuesMehrfach] = useState('');
  // Welche Projekte sind dadurch tatsächlich freigegeben? Ohne diese Liste
  // tippt man einen Präfix ein und erfährt nie, ob er greift – genau daran
  // ist die Funktion in der Praxis gescheitert.
  const [freigegebene, setFreigegebene] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/settings');
      setSettings(r.data);
      setPrefixes(r.data.irrelevant_project_prefixes || []);
      setDailyDefault(r.data.daily_hours_default ?? 8);
      setDailyLimit(r.data.daily_hours_soft_limit ?? 8);
      setLockDays(r.data.time_entry_lock_days ?? 30);
      setMehrfachPraefixe(r.data.multi_cluster_project_prefixes || []);
      try {
        const pr = await api.get('/projects', { params: { include_archived: true } });
        setFreigegebene((pr.data || []).filter(p => p.allow_multiple_per_day));
      } catch { setFreigegebene([]); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addPrefix = () => {
    const val = newPrefix.trim();
    if (!val) return;
    if (prefixes.includes(val)) { toast.error('Prefix bereits vorhanden.'); return; }
    setPrefixes([...prefixes, val]);
    setNewPrefix('');
  };
  const removePrefix = (p) => setPrefixes(prefixes.filter(x => x !== p));

  const addMehrfach = () => {
    const val = neuesMehrfach.trim();
    if (!val) return;
    if (mehrfachPraefixe.includes(val)) { toast.error('Prefix bereits vorhanden.'); return; }
    setMehrfachPraefixe([...mehrfachPraefixe, val]);
    setNeuesMehrfach('');
  };
  const removeMehrfach = (p) => setMehrfachPraefixe(mehrfachPraefixe.filter(x => x !== p));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/settings', {
        irrelevant_project_prefixes: prefixes,
        daily_hours_default: Number(String(dailyDefault).replace(',', '.')),
        daily_hours_soft_limit: Number(String(dailyLimit).replace(',', '.')),
        time_entry_lock_days: parseInt(lockDays, 10) || 0,
        multi_cluster_project_prefixes: mehrfachPraefixe,
      });
      toast.success('Einstellungen gespeichert.');
      await load();
    } catch (err) { toast.error(extractError(err)); } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="text-slate-500 text-sm">Lädt Einstellungen…</div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-lg flex items-center gap-2"><Clock size={18}/>Stunden-Standardwerte</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
            <div>
              <Label>Default-Stunden pro neuem Einsatz</Label>
              <Input
                type="number" step="0.25" min="0.25" max="24"
                value={dailyDefault}
                onChange={e => setDailyDefault(e.target.value)}
                data-testid="daily-hours-default"
              />
              <p className="text-xs text-slate-500 mt-1">Wird beim Anlegen neuer Einsätze vorbelegt.</p>
            </div>
            <div>
              <Label>Warngrenze pro Tag/Mitarbeiter</Label>
              <Input
                type="number" step="0.25" min="1" max="24"
                value={dailyLimit}
                onChange={e => setDailyLimit(e.target.value)}
                data-testid="daily-hours-limit"
              />
              <p className="text-xs text-slate-500 mt-1">Bei Überschreitung wird eine Warnung erzeugt (kein hartes Limit).</p>
            </div>
          </div>
          <div className="max-w-xl border-t border-slate-100 pt-4">
            <Label>Zeitsperre für Projektleiter (Tage rückwirkend)</Label>
            <Input
              type="number" step="1" min="0" max="365"
              value={lockDays}
              onChange={e => setLockDays(e.target.value)}
              className="max-w-[200px]"
              data-testid="time-lock-days"
            />
            <p className="text-xs text-slate-500 mt-1">
              Projektleiter dürfen Einsätze/Zeiten nur bis zu dieser Anzahl Tage in der Vergangenheit erfassen oder ändern. Danach ist der Tag gesperrt (nur Admin kann noch ändern). 0 = keine Sperre. Standard: 30 Tage (monatlicher Rhythmus).
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-lg flex items-center gap-2"><AlertTriangle size={18}/>Statistik-irrelevante Projekt-Prefixes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Projekte deren Projektnummer mit einem dieser Prefixes beginnt, werden bei Statistiken/Budgets nicht mitgezählt (z.B. Werkstatt/Wartung/Reparatur).
            Sie zählen aber weiterhin für die Tagesstunden der Mitarbeiter.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {prefixes.length === 0 && <div className="text-xs text-slate-400 italic">Keine Prefixes definiert.</div>}
            {prefixes.map(p => (
              <Badge key={p} variant="outline" className="font-mono text-sm py-1 gap-1" data-testid={`prefix-${p}`}>
                {p}
                <button
                  onClick={() => removePrefix(p)}
                  className="ml-1 text-slate-400 hover:text-rose-600"
                  data-testid={`prefix-remove-${p}`}
                >
                  <X size={12}/>
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 max-w-md">
            <Input
              placeholder="z.B. 0001"
              value={newPrefix}
              onChange={e => setNewPrefix(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addPrefix()}
              className="font-mono"
              data-testid="prefix-input"
            />
            <Button size="sm" variant="outline" onClick={addPrefix} data-testid="prefix-add-button"><Plus size={14} className="mr-1"/>Hinzufügen</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Plus size={18}/>Mehrfach planbare Projekte
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Solche Projekte dürfen an einem Tag mehrfach als eigener Block in der Plantafel
            stehen – mit eigener Besetzung und eigener Notiz. Gedacht für Sammelprojekte wie
            Reparatur oder Wartung: Das sind an einem Tag oft mehrere getrennte Aufträge.
            In der Plantafel erscheint bei diesen Projekten ein <span className="font-bold">+</span> am
            Projektkopf; damit wird ein weiterer Block angelegt.
          </p>
          <p className="text-sm text-slate-600">
            Der verlässliche Weg ist das Häkchen <span className="italic">„Mehrfach pro Tag planbar"</span> direkt
            am Projekt (Projekte → Bearbeiten). Die Prefix-Liste hier wirkt zusätzlich und
            automatisch für alle Projekte, deren Nummer so beginnt – praktisch für neu
            angelegte Sammelprojekte.
          </p>
          <p className="text-xs text-slate-500">
            Bei einer echten Baustelle ist ein zweiter Block fast immer ein Versehen – deshalb
            ist die Möglichkeit nicht überall vorhanden.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {mehrfachPraefixe.length === 0 && (
              <div className="text-xs text-slate-400 italic">Keine – jedes Projekt nur einmal pro Tag.</div>
            )}
            {mehrfachPraefixe.map(p => (
              <Badge key={p} variant="outline" className="font-mono text-sm py-1 gap-1" data-testid={`mehrfach-${p}`}>
                {p}
                <button onClick={() => removeMehrfach(p)} className="ml-1 text-slate-400 hover:text-rose-600"
                        data-testid={`mehrfach-remove-${p}`}>
                  <X size={12}/>
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 max-w-md">
            <Input
              placeholder="z.B. 0005"
              value={neuesMehrfach}
              onChange={e => setNeuesMehrfach(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addMehrfach()}
              className="font-mono"
              data-testid="mehrfach-input"
            />
            <Button size="sm" variant="outline" onClick={addMehrfach} data-testid="mehrfach-add-button">
              <Plus size={14} className="mr-1"/>Hinzufügen
            </Button>
          </div>

          {/* Kontrollanzeige. Erst nach dem Speichern aktuell – deshalb der
              Hinweis. Ohne diese Liste bleibt unklar, ob ein eingetippter
              Prefix überhaupt ein Projekt trifft. */}
          <div className="border-t border-slate-100 pt-3">
            <Label className="text-sm">Derzeit freigegeben</Label>
            {freigegebene === null && <p className="text-xs text-slate-400 mt-1">Lädt…</p>}
            {freigegebene && freigegebene.length === 0 && (
              <p className="text-xs text-amber-700 mt-1" data-testid="mehrfach-keine">
                Kein einziges Projekt. Dann gibt es in der Plantafel auch nirgends das +.
                Prüfe, ob die Prefixes zu den echten Projektnummern passen – oder setze das
                Häkchen direkt am Projekt.
              </p>
            )}
            {freigegebene && freigegebene.length > 0 && (
              <ul className="mt-1 space-y-0.5" data-testid="mehrfach-liste">
                {freigegebene.map(p => (
                  <li key={p.id} className="text-xs text-slate-600 flex items-center gap-2">
                    <span className="font-mono">{p.project_number || '—'}</span>
                    <span className="truncate">{p.name}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {p.mehrfach_aus_praefix ? 'über Projektnummer' : 'Häkchen am Projekt'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-slate-400 mt-1">
              Die Liste wird nach dem Speichern neu geladen.
            </p>
          </div>
          <div className="pt-2">
            <Button onClick={save} disabled={saving} data-testid="settings-save-button">
              <Save size={14} className="mr-1"/>{saving ? 'Speichere…' : 'Einstellungen speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ================= System =================
function SystemSettings() {
  const fileInputRef = React.useRef(null);
  const { confirm } = useConfirm();

  const exportBackup = async () => {
    try {
      const r = await api.get('/export/backup');
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `friedrichs-dach-backup-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Backup exportiert.');
    } catch (err) { toast.error(extractError(err)); }
  };

  const importBackup = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!(await confirm({
        title: 'Backup importieren?',
        description: 'ACHTUNG: Dies überschreibt alle aktuellen Daten (außer Benutzer).',
        variant: 'destructive', confirmLabel: 'Importieren',
      }))) return;
      await api.post('/import/backup', data);
      toast.success('Backup importiert. Bitte Seite neu laden.');
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) { toast.error(extractError(err) || 'Fehler beim Import.'); }
    finally { e.target.value = ''; }
  };

  const resetDemo = async () => {
    if (!(await confirm({
      title: 'Demo-Daten zurücksetzen?',
      description: 'ACHTUNG: Alle Kostenstellen, Mitarbeiter, Projekte, Einsätze und Abwesenheiten werden gelöscht und durch Demo-Daten ersetzt. Benutzer bleiben erhalten.',
      variant: 'destructive', confirmLabel: 'Zurücksetzen',
    }))) return;
    try {
      await api.post('/system/reset-demo');
      toast.success('Demo-Daten zurückgesetzt. Seite wird neu geladen.');
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-display text-lg flex items-center gap-2"><Download size={18}/>Datensicherung</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-slate-200 p-4">
            <div className="font-semibold mb-1">Komplettes Backup exportieren</div>
            <p className="text-sm text-slate-600 mb-3">Lädt alle Daten (Kostenstellen, Mitarbeiter, Projekte, Einsätze, Abwesenheiten, Benutzer) als JSON-Datei herunter.</p>
            <Button onClick={exportBackup} data-testid="export-backup-button"><Download size={14} className="mr-1"/>Alle Daten exportieren</Button>
          </div>
          <div className="rounded-md border border-slate-200 p-4">
            <div className="font-semibold mb-1">Backup importieren</div>
            <p className="text-sm text-slate-600 mb-3">Stellt ein zuvor exportiertes Backup wieder her. <span className="text-rose-600 font-semibold">Achtung: Überschreibt aktuelle Daten!</span></p>
            <Button onClick={() => fileInputRef.current?.click()} variant="outline" data-testid="import-backup-button"><Upload size={14} className="mr-1"/>JSON-Datei auswählen</Button>
            <input ref={fileInputRef} type="file" accept=".json" onChange={importBackup} className="hidden"/>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
